import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    Paper,
} from '@mui/material';
import MuiModal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { ExportTypes, WorkflowTypes } from '~/state/atoms/viewerAtoms';

export type TabItem = {
    name: JSX.Element | string;
    description: JSX.Element | string;
    content: React.ReactNode;
    type: WorkflowTypes | ExportTypes;
};

type TabModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    items: TabItem[];
    selectedPage: WorkflowTypes | ExportTypes | null;
    setSelectedPage: (selectedPage: WorkflowTypes | ExportTypes) => void;
};

export default function TabModal({
    open,
    setOpen,
    items,
    selectedPage,
    setSelectedPage,
}: TabModalProps) {
    return (
        <MuiModal open={open} onClose={() => setOpen(false)} disableAutoFocus>
            <Box
                sx={(theme) => ({
                    position: 'absolute' as const,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(4, 30, 52, 0.65)',
                    borderRadius: `${theme.shape.borderRadius}px`,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.27)',
                    backdropFilter: 'blur(5px)',
                    WebkitBackdropFilter: 'blur(5px)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    p: 1,
                })}
            >
                <Box
                    display="grid"
                    gridTemplateColumns="160px 1fr"
                    gap={1}
                    width="60vw"
                    minWidth={800}
                    position="relative"
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        <List sx={{ p: 0, display: 'grid', gap: 1 }}>
                            {items.map((item) => (
                                <ListItem
                                    key={item.type}
                                    sx={{ p: 0 }}
                                    color="primary"
                                >
                                    <ListItemButton
                                        sx={(theme) => ({
                                            width: '100%',
                                            display: 'grid',
                                            borderRadius: `${theme.shape.borderRadius}px`,
                                            p: 1,
                                        })}
                                        color="primary"
                                        onClick={() =>
                                            setSelectedPage(item.type)
                                        }
                                        selected={selectedPage === item.type}
                                    >
                                        <Typography
                                            fontSize={14}
                                            fontWeight={500}
                                        >
                                            {item.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontSize={11}
                                        >
                                            {item.description}
                                        </Typography>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>{' '}
                    <Paper sx={{ p: 3, flex: 1, height: 600 }} elevation={0}>
                        <IconButton
                            data-testid="closeModalButton"
                            onClick={() => setOpen(false)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                            }}
                        >
                            <X size={21} />
                        </IconButton>
                        <AnimatePresence mode="wait">
                            {items.map(
                                (item) =>
                                    selectedPage === item.type && (
                                        <motion.div
                                            key={item.type}
                                            initial={{ opacity: 0, x: 10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 10 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {item.content}
                                        </motion.div>
                                    )
                            )}
                        </AnimatePresence>
                    </Paper>
                </Box>
            </Box>
        </MuiModal>
    );
}
